import * as React from 'react'

import type { Row } from '@tanstack/react-table'

import BandRow, {
  BAND_ROW_MIN_HEIGHT,
} from '~/client/src/shared/components/SharedVirtualTable/BandRow'
import SharedVirtualTable from '~/client/src/shared/components/SharedVirtualTable/SharedVirtualTable'
import type { ListNode } from '~/client/src/shared/lists/core'

import ListBandHeader from './ListBandHeader'
import type { UseTableListResult } from './useTableList'

export interface TableBandPresentation {
  readonly id: string
  readonly optionId: string
  readonly label: string
  readonly count: number
  readonly expanded: boolean
  readonly selectable: boolean
  readonly selected: boolean
  readonly indeterminate: boolean
  readonly onToggleSelection: () => void
  readonly onToggleExpand: () => void
}

interface IDesktopListTableProps<T> {
  readonly list: UseTableListResult<T>
  readonly ariaLabel: string
  readonly renderBand?: (band: TableBandPresentation) => React.ReactNode
  readonly onActivate?: (record: T) => void
  readonly fillContainerWidth?: boolean
  readonly emptyState?: React.ReactNode
}

function renderDefaultBand(band: TableBandPresentation): React.ReactNode {
  return <ListBandHeader band={band} />
}

const RECORD_ROW_HEIGHT = 40

function estimateRowHeight<T>(row: Row<ListNode<T>>): number {
  return row.original.kind === 'record'
    ? RECORD_ROW_HEIGHT
    : BAND_ROW_MIN_HEIGHT
}

export default function DesktopListTable<T>({
  list,
  ariaLabel,
  renderBand = renderDefaultBand,
  onActivate,
  fillContainerWidth = false,
  emptyState,
}: IDesktopListTableProps<T>): React.ReactElement {
  const renderRow = (
    row: Row<ListNode<T>>,
    defaultCells: React.ReactNode[],
  ): React.ReactNode => {
    const node = row.original
    if (node.kind === 'record') return defaultCells

    const selectionState = list.model.bandSelection.get(node.id)
    return (
      <BandRow table={list.table}>
        {renderBand({
          id: node.id,
          optionId: node.optionId,
          label: node.label,
          count: node.count,
          expanded: !node.collapsed,
          selectable: list.selection.selectable,
          selected: selectionState === 'all',
          indeterminate: selectionState === 'partial',
          onToggleSelection: () =>
            list.selection.toggleBand(node.id, node.selectableIds),
          onToggleExpand: () => list.bands.toggle(node.id),
        })}
      </BandRow>
    )
  }

  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-hidden">
      <SharedVirtualTable
        table={list.table}
        ariaLabel={ariaLabel}
        renderRow={renderRow}
        estimateRowHeight={estimateRowHeight}
        fillContainerWidth={fillContainerWidth}
        emptyState={emptyState}
        columnWidths={list.columnWidths}
        onColumnWidthsChange={list.onColumnWidthsChange}
        onRowClick={(row, event) => {
          const node = row.original
          if (node.kind === 'record') {
            const shouldActivate = list.activateRecord(node.recordId, event)
            if (shouldActivate) onActivate?.(node.record)
          }
        }}
      />
    </div>
  )
}
